import { useNavigate } from 'react-router';
import { Award, BookOpen, CheckCircle2, GraduationCap, Loader2 } from 'lucide-react';
import { useGetTrainingCoursesQuery } from '../../store/api/apiSlice';
import type { TrainingCourseSummary } from '../../types';
import { readableError } from './lib';

function StatusBadge({ course }: { course: TrainingCourseSummary }) {
    if (course.certified) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Certified
            </span>
        );
    }
    if (course.status === 'in_progress') {
        return (
            <span className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-[#FF6B35]">
                In progress
            </span>
        );
    }
    return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
            Not started
        </span>
    );
}

function CourseCard({ course }: { course: TrainingCourseSummary }) {
    const navigate = useNavigate();
    const pct = course.totalVideos > 0 ? Math.round((course.passedVideos / course.totalVideos) * 100) : 0;

    return (
        <button
            type="button"
            onClick={() => navigate(`/training/${course.id}`)}
            className="flex flex-col rounded-2xl border border-gray-100 bg-white p-6 text-left transition-shadow hover:shadow-md"
        >
            <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50">
                    <BookOpen className="h-5 w-5 text-[#FF6B35]" />
                </div>
                <StatusBadge course={course} />
            </div>
            <h3 className="mb-1 text-lg font-bold text-gray-900">{course.title}</h3>
            {course.description && <p className="mb-4 line-clamp-2 text-sm text-gray-500">{course.description}</p>}

            <div className="mt-auto">
                <div className="mb-1.5 flex items-center justify-between text-xs text-gray-500">
                    <span>
                        {course.passedVideos}/{course.totalVideos} lessons
                    </span>
                    <span>Pass mark {course.passingScore}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                        className={`h-full rounded-full ${course.certified ? 'bg-green-500' : 'bg-[#FF6B35]'}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>
        </button>
    );
}

export default function Training() {
    const navigate = useNavigate();
    const { data: courses, isLoading, isError, error } = useGetTrainingCoursesQuery();

    return (
        <div className="p-4 md:p-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 md:text-3xl">
                        <GraduationCap className="h-7 w-7 text-[#FF6B35]" /> Training
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Complete each course in order to earn your certification.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/training/certificates')}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50"
                >
                    <Award className="h-4 w-4 text-[#FF6B35]" /> My certificates
                </button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
                </div>
            ) : isError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                    {readableError(error, 'Could not load courses.')}
                </div>
            ) : !courses || courses.length === 0 ? (
                <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center">
                    <BookOpen className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                    <p className="font-medium text-gray-900">No courses yet</p>
                    <p className="mt-1 text-sm text-gray-500">Training courses will appear here once published.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {courses.map((course) => (
                        <CourseCard key={course.id} course={course} />
                    ))}
                </div>
            )}
        </div>
    );
}
